import { useState, type FormEvent, type ReactNode } from 'react';
import { ServerCog } from 'lucide-react';
import { Banner, Button, Checkbox, Dialog, Field, Input, Select } from '@/components/ui';
import { useRestoreToHost } from '@/hooks/useBackups';
import { useNodes } from '@/hooks/useNodes';
import { toast } from '@/store/toastStore';
import { errorField, errorText } from '@/utils/errors';
import { formatDateTime } from '@/utils/format';
import type { BackupListItem } from '@/types/api';

/**
 * "Развернуть на сервере" (ADR-0033): a watched site's copy put on its own
 * server, over what is there — a copy of which is taken first — or on another
 * external server standing in for a lost one, with its names pointed there
 * when asked. Confirmed by typing the site's domain.
 */
export function RestoreToHostDialog({ backup, onClose }: { readonly backup: BackupListItem; readonly onClose: () => void }): ReactNode {
  const nodes = useNodes({ watch: false });
  const hosts = (nodes.data ?? []).filter((node) => node.role === 'host' && node.agentVersion !== null);
  const [target, setTarget] = useState(backup.siteHostId ?? hosts[0]?.id ?? '');
  const [switchDns, setSwitchDns] = useState(true);
  const [typed, setTyped] = useState('');

  const restore = useRestoreToHost({
    onSuccess: () => {
      toast.success('Развёртывание копии поставлено в очередь');
      onClose();
    },
  });

  const domain = backup.siteDomain ?? '';
  const sameServer = target !== '' && target === backup.siteHostId;
  const matches = typed.trim().toLowerCase() === domain;
  const field = restore.isError ? errorField(restore.error) : null;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (matches && target) restore.mutate({ backupId: backup.id, input: { targetNodeId: target, switchDns: !sameServer && switchDns, confirmation: typed.trim() } });
  }

  return (
    <Dialog
      open
      title="Развернуть копию на сервере"
      description={`${backup.siteName} · ${formatDateTime(backup.createdAt)}`}
      onClose={() => (restore.isPending ? undefined : onClose())}
      footer={
        <>
          <Button onClick={onClose} disabled={restore.isPending}>
            Отмена
          </Button>
          <Button variant="danger-solid" icon={ServerCog} busy={restore.isPending} disabled={!matches || !target} type="submit" form="restore-to-host">
            Развернуть
          </Button>
        </>
      }
    >
      <form id="restore-to-host" className="stack" onSubmit={submit}>
        {hosts.length === 0 ? (
          <Banner tone="warning" icon={ServerCog}>
            Нет подключённых внешних серверов.
          </Banner>
        ) : null}
        <Field label="Сервер" htmlFor="restore-target" {...(field === 'targetNodeId' ? { error: errorText(restore.error) } : {})}>
          <Select
            id="restore-target"
            label="Сервер"
            value={target}
            onChange={setTarget}
            options={hosts.map((host) => ({
              value: host.id,
              label: host.id === backup.siteHostId ? `${host.name} — текущий сервер сайта` : host.name,
              hint: host.ipv4 ?? '',
            }))}
            placeholder="Выберите сервер"
          />
        </Field>
        {sameServer ? (
          <p className="field-hint">
            Текущее содержимое будет заменено. Перед этим создаётся копия текущего состояния.
          </p>
        ) : target ? (
          <>
            <p className="field-hint">
              Разворачиваются файлы, база данных и конфигурация nginx. Предварительно проверяются nginx, MySQL и место на диске.
            </p>
            <Checkbox
              checked={switchDns}
              onChange={setSwitchDns}
              label="Переключить домены на этот сервер"
              hint="A-записи в 2d2s для доменов с разрешённым тегом; остальные — вручную"
            />
          </>
        ) : null}
        <Field label="Для подтверждения введите домен сайта" htmlFor="restore-confirm" hint={domain}>
          <Input id="restore-confirm" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder={domain} autoComplete="off" />
        </Field>
        {restore.isError && field !== 'targetNodeId' ? (
          <p className="field-error" role="alert">
            {errorText(restore.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
